import Loader from "@components/global/Loader/Loader";
import { ALGOS_E } from "@constants/Algos";
import {
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import cn from "classnames";
import PointAnotation from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
// import zoomPlugin from 'chartjs-plugin-zoom';
// import "moment/locale/ru";
import { Line } from "react-chartjs-2";
import { useIntl } from "react-intl";
import DatePicker from "../global/DatePicker";
import { IChartLiveDataSource } from "./ChartLiveDataSource";
import styles from "./Charts.module.scss";
import useCleanChartControl, {
  ChartScales,
  CleanChartDataset,
} from "./CleanChart.control";
import { IChartPeriodControl } from "./CleanChartPeriod.control";
import DayChooser from "./DayChooser";
import LastChartInfo from "./LastChartInfo";
import { Flex } from "antd";
import { Button } from 'antd';
import { DownloadOutlined } from "@ant-design/icons";
const { RangePicker } = DatePicker;

interface CleanChartProps<T> {
  id?: number,
  readonly scale?: ChartScales | null;
  readonly showLegend: boolean;
  readonly showY1Scales: boolean;
  readonly isLoading: boolean;
  readonly datasets: CleanChartDataset[];
  readonly poolHash?: ALGOS_E;
  readonly liveDataSource: IChartLiveDataSource<T>;
  readonly periodControl: IChartPeriodControl;
  readonly defaultSpeed?: number;
  readonly speedDrops?: number;
  readonly chartClassName?: string;
  readonly className?: string;

  readonly visibilityTimeoutMs?: number;
  readonly tag?: string;
  readonly showExport?: boolean;

  onVisible?: () => void;

  children?: React.ReactNode;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTitle,
  Tooltip,
  Legend,
  TimeScale,
  ChartDataLabels,
  PointAnotation
  //zoomPlugin
);

function CleanChart<T>({
  id,
  scale,
  showLegend,
  showY1Scales,
  isLoading,
  datasets,
  liveDataSource,
  periodControl,
  defaultSpeed,
  speedDrops, // calculated! not percent
  visibilityTimeoutMs,
  poolHash = ALGOS_E.SHA256,
  onVisible,
  children,
  tag,
  chartClassName,
  showExport,
  className,
}: CleanChartProps<T>): JSX.Element {
  const { formatMessage } = useIntl();

  const control = useCleanChartControl({
    id,
    scale,
    showLegend,
    showY1Scales,
    periodControl,
    liveDataSource,
    rawDatasets: datasets,
    poolHash,
    defaultSpeed,
    speedDrops,
    visibilityTimeoutMs,
    tag,
    onVisible
  });

  return (
    <div className={cn(styles.chart__container, className)}>
      {isLoading && <Loader absolute={true} />}
      <Flex gap={24} align="center">
        {children}
        {control.isShowPeriod && (
          <div className={styles.time_period__container}>
            {/*<label>{formatMessage({ id: "chart_hash.period" })}</label>*/}
            <RangePicker
              format={"DD.MM.YYYY"}
              allowEmpty={[false, false]}
              allowClear={true}
              disabledDate={control.isDayDisabled}
              value={control.pickerValue}
              // defaultValue={control.pickerValue}
              className={styles.picker}
              onChange={control.onPickerPeriodChanged}
            />
          </div>
        )}
        {showExport && (
          <Button
            onClick={() => control.exportToCSV(tag ?? "chart-data")}
            size="small"
          >
            <DownloadOutlined />
            CSV
          </Button>
        )}
      </Flex>
      <div className={cn(styles.chart_wrapper, chartClassName)}>
        {!isLoading && (
          <>
            {control.isNotEmpty && (
              <LastChartInfo
                isHide={control.infoVisibility}
                defaultSpeed={defaultSpeed}
                speedDrops={speedDrops}
                timeAndHash={control.lastPoint}
                coords={control.coords}
                hashAlgo={poolHash}
              />
            )}
            <h4>{control.axisYName}</h4>
            <div
              style={{
                position: "relative",
                //inset: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <Line
                className="cleanChartCanvas"
                data={control.chartData}
                options={control.chartOptions}
                plugins={control.plugins}
                ref={control.chartRef}
                onMouseLeave={control.hideInfo}
                onMouseEnter={control.showInfo}
              // redraw
              />
              {!control.hasData && (
                <div className={styles.empty}>
                  <h3>{formatMessage({ id: "chart_hash.empty_data" })}</h3>
                </div>
              )}
              <div
                className={styles.shimmer}
                style={{
                  opacity: 0,
                  zIndex: -1,
                }}
                ref={control.shimmerRef}
              ></div>
            </div>
          </>
        )}
      </div>
      {control.currentDay && (
        <DayChooser
          currentDay={control.currentDay}
          disabled={isLoading}
          onDayChanged={control.setCurrentDay}
        />
      )}
    </div>
  );
}

export default CleanChart;
